import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineClipboardDocumentList
} from 'react-icons/hi2';
import {
  authService,
  rcaService,
  masterDataService,
  userService
} from '../services';
import { usePermissions } from '../hooks/usePermissions';
import SharedModal from '../components/common/SharedModal';
import PageLoading from "../components/common/PageLoading";

const RCAActions = () => {
  const [actions, setActions] = useState([]);
  const [rcaRecords, setRcaRecords] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [formData, setFormData] = useState({
    rcaId: '',
    description: '',
    actionType: '',
    assignedTo: '',
    dueDate: '',
    statusId: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission('RCAAction.View');
  const canCreate = hasPermission('RCAAction.Create');
  const canEdit = hasPermission('RCAAction.Edit');
  const canDelete = hasPermission('RCAAction.Delete');

  console.log('canView', canView);
  console.log('canCreate', canCreate);
  console.log('canEdit', canEdit);
  console.log('canDelete', canDelete);

  useEffect(() => {
    if (!tenantId) {
      setError('Please login to view RCA actions');
      setLoading(false);
      return;
    }
    if (!canView) {
      setError('You do not have permission to view RCA actions');
      setLoading(false);
      return;
    }
    loadInitialData();
  }, [tenantId, canView]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load all RCA records
      const rcaData = await rcaService.getRcaRecords();
      setRcaRecords(rcaData || []);

      // Load statuses
      const statusesData = await masterDataService.getStatuses?.(tenantId) || [];
      setStatuses(statusesData);

      // Load users
      const usersData = await userService.getAllUsers?.() || [];
      setUsers(usersData);

      // Load all actions from all RCA records
      await loadAllActions(rcaData || []);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadAllActions = async (rcaList) => {
    try {
      const allActions = [];
      for (const rca of rcaList) {
        try {
          const actionsData = await rcaService.getRcaActionsByRca(rca.rcaId);
          if (actionsData && actionsData.length > 0) {
            allActions.push(...actionsData.map(action => ({
              ...action,
              rcaTitle: rca.triggerReason || `RCA ${rca.rcaId}`
            })));
          }
        } catch (err) {
          console.error(`Error loading actions for RCA ${rca.rcaId}:`, err);
        }
      }
      setActions(allActions);
    } catch (err) {
      console.error('Error loading all actions:', err);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredActions = actions.filter((action) =>
    action.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    action.rcaTitle?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    if (!canCreate) {
      setError('You do not have permission to create RCA actions');
      return;
    }
    setEditingAction(null);
    setFormData({
      rcaId: '',
      description: '',
      actionType: '',
      assignedTo: '',
      dueDate: '',
      statusId: ''
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (action) => {
    if (!canEdit) {
      setError('You do not have permission to edit RCA actions');
      return;
    }
    setEditingAction(action);
    setFormData({
      rcaId: action.rcaId?.toString() || '',
      description: action.description || '',
      actionType: action.actionType || '',
      assignedTo: action.assignedTo?.toString() || '',
      dueDate: action.dueDate ? new Date(action.dueDate).toISOString().split('T')[0] : '',
      statusId: action.statusId?.toString() || ''
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (actionId) => {
    if (!canDelete) {
      setError('You do not have permission to delete RCA actions');
      return;
    }
    try {
      setError('');
      await rcaService.deleteRcaAction(actionId);
      await loadInitialData();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete RCA action. Please try again.');
      console.error('Error deleting RCA action:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.rcaId || formData.rcaId === '') {
      errors.rcaId = 'RCA record is required';
    }
    if (!formData.description?.trim()) {
      errors.description = 'Description is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check permissions before submitting
    if (editingAction && !canEdit) {
      setError('You do not have permission to edit RCA actions');
      return;
    }
    if (!editingAction && !canCreate) {
      setError('You do not have permission to create RCA actions');
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const actionData = {
        rcaId: parseInt(formData.rcaId, 10),
        description: formData.description.trim(),
        actionType: formData.actionType?.trim() || null,
        assignedTo: formData.assignedTo ? parseInt(formData.assignedTo, 10) : null,
        dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null,
        statusId: formData.statusId ? parseInt(formData.statusId, 10) : null
      };

      if (editingAction) {
        actionData.actionId = editingAction.actionId;
        await rcaService.updateRcaAction(editingAction.actionId, actionData);
      } else {
        await rcaService.createRcaAction(actionData);
      }

      await loadInitialData();
      setShowModal(false);
      setFormData({
        rcaId: '',
        description: '',
        actionType: '',
        assignedTo: '',
        dueDate: '',
        statusId: ''
      });
      setEditingAction(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save RCA action. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAction(null);
    setFormData({
      rcaId: '',
      description: '',
      actionType: '',
      assignedTo: '',
      dueDate: '',
      statusId: ''
    });
    setFormErrors({});
  };

  const getRcaTitle = (rcaId) => {
    if (!rcaId) return '-';
    const rca = rcaRecords.find((r) => r.rcaId === rcaId);
    return rca ? (rca.triggerReason || `RCA ${rcaId}`) : `RCA ${rcaId}`;
  };

  const getStatusName = (statusId) => {
    if (!statusId) return '-';
    const status = statuses.find((s) => s.statusId === statusId);
    return status ? status.statusName : `Status ${statusId}`;
  };

  const getUserName = (userId) => {
    if (!userId) return '-';
    const user = users.find((u) => u.userId === userId);
    return user ? (user.userName || user.fullName || `User ${userId}`) : `User ${userId}`;
  };

  if (loading) {
    return (
      <div className="rca-actions-page">
        <PageLoading message="Loading RCA actions..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer rca-actions-page">
      <div className="page-header">
        <div className="header-content">
          <h1>RCA Actions</h1>
          <p>Manage actions for RCA records</p>
        </div>
        <div className="header-actions">
          {canCreate && (
            <button className="btn-primary" onClick={handleAdd}>
              <HiOutlinePlus className="btn-icon" />
              Add Action
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search actions..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredActions.length} {filteredActions.length === 1 ? 'action' : 'actions'}
          </div>
        </div>

        {filteredActions.length === 0 ? (
          <div className="empty-state">
            <HiOutlineClipboardDocumentList className="empty-icon" />
            <h3>No RCA Actions</h3>
            <p>
              {searchTerm
                ? 'No actions match your search criteria.'
                : canCreate
                ? 'Get started by creating your first RCA action.'
                : 'No RCA actions found.'}
            </p>
            {canCreate && !searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                <HiOutlinePlus className="btn-icon" />
                Create First Action
              </button>
            )}
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Actions</th>
                  <th>RCA Record</th>
                  <th>Description</th>
                  <th>Action Type</th>
                  <th>Assigned To</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th>Completed At</th>
                </tr>
              </thead>
              <tbody>
                {filteredActions.map((action) => (
                  <tr key={action.actionId}>
                    <td>
                      <div className="action-buttons">
                        {canEdit && (
                          <button
                            className="btn-icon-edit"
                            onClick={() => handleEdit(action)}
                            title="Edit action"
                          >
                            <HiOutlinePencil />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="btn-icon-delete"
                            onClick={() => setDeleteConfirm(action)}
                            title="Delete action"
                          >
                            <HiOutlineTrash />
                          </button>
                        )}
                      </div>
                    </td>
                    <td>{action.rcaTitle || getRcaTitle(action.rcaId)}</td>
                    <td>{action.description || '-'}</td>
                    <td>{action.actionType || '-'}</td>
                    <td>{getUserName(action.assignedTo)}</td>
                    <td>
                      {action.dueDate
                        ? new Date(action.dueDate).toLocaleDateString()
                        : '-'}
                    </td>
                    <td>{getStatusName(action.statusId)}</td>
                    <td>
                      {action.completedAt
                        ? new Date(action.completedAt).toLocaleDateString()
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingAction ? 'Edit RCA Action' : 'Add RCA Action'}
      >
        <div className="shared-modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>
                RCA Record <span className="required">*</span>
              </label>
              <select
                value={formData.rcaId}
                onChange={(e) => {
                  setFormData({ ...formData, rcaId: e.target.value });
                  setFormErrors({ ...formErrors, rcaId: '' });
                }}
                disabled={!!editingAction}
                className={formErrors.rcaId ? 'error' : ''}
              >
                <option value="">Select RCA Record</option>
                {rcaRecords.map((rca) => (
                  <option key={rca.rcaId} value={rca.rcaId}>
                    {rca.triggerReason || `RCA ${rca.rcaId}`}
                  </option>
                ))}
              </select>
              {formErrors.rcaId && (
                <span className="error-text">{formErrors.rcaId}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                Description <span className="required">*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => {
                  setFormData({ ...formData, description: e.target.value });
                  setFormErrors({ ...formErrors, description: '' });
                }}
                rows="4"
                className={formErrors.description ? 'error' : ''}
                placeholder="Enter action description"
              />
              {formErrors.description && (
                <span className="error-text">{formErrors.description}</span>
              )}
            </div>

            <div className="form-group">
              <label>Action Type</label>
              <input
                type="text"
                value={formData.actionType}
                onChange={(e) => {
                  setFormData({ ...formData, actionType: e.target.value });
                }}
                placeholder="Enter action type (optional)"
              />
            </div>

            <div className="form-group">
              <label>Assigned To</label>
              <select
                value={formData.assignedTo}
                onChange={(e) => {
                  setFormData({ ...formData, assignedTo: e.target.value });
                }}
              >
                <option value="">Select User</option>
                {users.map((user) => (
                  <option key={user.userId} value={user.userId}>
                    {user.userName || user.fullName || `User ${user.userId}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Due Date</label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => {
                  setFormData({ ...formData, dueDate: e.target.value });
                }}
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={formData.statusId}
                onChange={(e) => {
                  setFormData({ ...formData, statusId: e.target.value });
                }}
              >
                <option value="">Select Status</option>
                {statuses.map((status) => (
                  <option key={status.statusId} value={status.statusId}>
                    {status.statusName}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-actions" style={{display: 'flex', justifyContent: 'flex-end', gap: '10px'}}>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseModal}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : editingAction ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete RCA Action"
        customClass="small"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.actionId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete this RCA action? This action
            cannot be undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default RCAActions;

